import {
  DocumentViewerState,
  ElementStatus,
  PageStructure,
  SelectedElementContainer,
  ToolMessage,
  useDocumentViewer,
  useDocumentViewerState,
  useLinearTransform,
  ViewElementContainer
} from '@st/document-viewer-kit'
import { toDragPointerEvent, useUpdateBounds } from '@st/document-viewer-kit/src/use-movable'
import { DocumentAnnotation, StampAnnotation } from '@st/sdk'
import { ArrowLeftIcon, ArrowRightIcon, FontSizeLargerIcon, FontSizeSmallerIcon } from '@st/theme'
import { DeleteIcon } from '~/components/icons'
import { AnnotationFloatingToolbar, FloatingToolbarButton } from '../../floating-toolbar'

import { StampView } from '@st/document-annotations'
import { HTMLSurface, Viewport } from '@st/document-rendering'
import { selAnnotations } from '@st/document-viewer-kit/src/document-viewer-module'
import { THEME_COLORS } from '@st/theme/colors'
import { cuid } from '@st/util/cuid'
import { useMemo, useState } from 'react'
import { StampTemplate, StampToolState } from '~/st-doc-viewer/annotations'
import { InlineColorPicker } from '~/theme'
import { recomputeStampLayoutAndStyle } from './stamp-layout'
import {
  findMatchingTemplate,
  REFERENCE_STAMP_TEMPLATE_SECTIONS,
  TICK_MARK_STAMP_TEMPLATE_SECTIONS
} from './templates'
import { VisualSelect, VisualSelectSection } from './visual-select'

export const STAMP_COLOR_OPTIONS = [
  THEME_COLORS['red'][600],
  THEME_COLORS['blue'][500],
  THEME_COLORS['orange'][500],
  THEME_COLORS['green'][500],
  THEME_COLORS['gray'][900]
]

export function StampToolEditor({
  toolState,
  activeAnnotation,
  send
}: {
  toolState: StampToolState
  activeAnnotation: StampAnnotation | undefined
  send: (message: ToolMessage<StampAnnotation, StampToolState>) => void
}) {
  const [isOpen, setIsOpen] = useState(false)

  switch (toolState.category) {
    case 'checkmark':
      return (
        <InlineColorPicker
          options={STAMP_COLOR_OPTIONS}
          value={toolState.template!.annotation.color}
          onChange={(color) => {
            if (activeAnnotation) {
              const newAnnotation: StampAnnotation = {
                ...activeAnnotation,
                color: color
              }
              send({
                type: 'updateAnnotationDraft',
                annotationDraft: newAnnotation
              })
              send({ type: 'updateTool', tool: copyToolState(newAnnotation) })
            } else {
              send({
                type: 'updateTool',
                tool: {
                  ...toolState,
                  template: {
                    ...toolState.template!,
                    annotation: { ...toolState.template!.annotation, color: color }
                  }
                }
              })
            }
          }}
        />
      )
    case 'cross_reference':
    case 'tick_mark':
      const isReferenceCategory = toolState.category === 'cross_reference'
      const sections = isReferenceCategory
        ? REFERENCE_STAMP_TEMPLATE_SECTIONS
        : TICK_MARK_STAMP_TEMPLATE_SECTIONS

      return (
        <VisualSelect
          className="w-64"
          isOpen={isOpen}
          onOpenChange={setIsOpen}
          placeholder={
            toolState.template ? (
              <div className="h-8">
                <StampOptionPreview template={toolState.template} />
              </div>
            ) : (
              <div className="flex h-8 items-center px-2 text-base font-normal text-gray-700">
                {isReferenceCategory ? 'Select a reference' : 'Select a stamp'}
              </div>
            )
          }
        >
          {sections.map((section) => (
            <VisualSelectSection key={section.title} title={section.title} layout={section.layout}>
              {section.templates.map((template, index) => (
                <div
                  key={index}
                  className="h-10 cursor-pointer hover:bg-gray-100"
                  onClick={() => {
                    setIsOpen(false)
                    send({
                      type: 'updateTool',
                      tool: {
                        name: 'stamp',
                        category: isReferenceCategory ? 'cross_reference' : 'tick_mark',
                        template
                      }
                    })
                  }}
                >
                  <StampOptionPreview
                    template={template}
                    showDescription={section.layout === 'rows'}
                  />
                </div>
              ))}
            </VisualSelectSection>
          ))}
        </VisualSelect>
      )
  }
}

export function copyToolState(annotation: StampAnnotation): StampToolState {
  const matchingTemplate = findMatchingTemplate(annotation)
  if (!matchingTemplate) {
    return { name: 'stamp', template: undefined, category: undefined }
  }

  return {
    name: 'stamp',
    category: matchingTemplate.category,
    template: {
      ...matchingTemplate,
      annotation: {
        ...matchingTemplate.annotation,
        color: annotation.color,
        stampSize: annotation.stampSize
      }
    }
  }
}

export function StampToolOverlay({
  page,
  toolState,
  activeAnnotation,
  status,
  send
}: {
  page: PageStructure
  toolState: StampToolState
  activeAnnotation: StampAnnotation | undefined
  status: ElementStatus
  send: (message: ToolMessage<StampAnnotation, StampToolState>) => void
}) {
  const transform = useLinearTransform()
  const [pointer, setPointer] = useState<{ x: number; y: number } | undefined>(undefined)

  const ghostAnnotation = useMemo(() => {
    if (!toolState.template) {
      return undefined
    }
    return recomputeStampLayoutAndStyle(toolState.template.annotation)
  }, [toolState.template])

  const cursor = useMemo(
    () => (!activeAnnotation && toolState.template ? 'default' : 'default'),
    [activeAnnotation, toolState.template]
  )

  return (
    <>
      <div
        style={{
          position: 'absolute',
          cursor: cursor,
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          backgroundColor: 'transparent'
        }}
        onPointerMove={(e) => {
          if (activeAnnotation || !toolState.template) {
            setPointer(undefined)
            return
          }
          const pointerEvent = toDragPointerEvent(e, transform, 'drawing-canvas')
          setPointer(pointerEvent.point)
        }}
        onPointerLeave={() => {
          setPointer(undefined)
        }}
        onPointerDown={(e) => {
          e.preventDefault()
          e.stopPropagation()

          const pointerEvent = toDragPointerEvent(e, transform, 'drawing-canvas')

          if (activeAnnotation) {
            send({ type: 'deselectAnnotation' })
            return
          } else if (toolState.template) {
            const baseAnnotation: StampAnnotation = recomputeStampLayoutAndStyle({
              ...toolState.template.annotation,
              id: cuid(),
              time: new Date().toISOString(),
              type: 'stamp',
              pageNumber: page.pageNumber,
              bounds: {
                x: pointerEvent.point.x,
                y: pointerEvent.point.y,
                width: 0,
                height: 0
              }
            })
            const annotation: StampAnnotation = {
              ...baseAnnotation,
              // we want to center this
              bounds: {
                width: baseAnnotation.bounds.width,
                height: baseAnnotation.bounds.height,
                x: baseAnnotation.bounds.x - baseAnnotation.bounds.width / 2,
                y: baseAnnotation.bounds.y - baseAnnotation.bounds.height / 2
              }
            }

            send({
              type: 'createAnnotationDraft',
              elementStatus: 'none',
              annotationDraft: annotation
            })
            send({ type: 'createAnnotation', annotationDraft: annotation })
          }
        }}
      />
      {ghostAnnotation && pointer && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            pointerEvents: 'none',
            transform: `translate(${pointer.x}px, ${pointer.y}px)`
          }}
        >
          <div style={{ transform: 'translate(-50%, -50%)' }}>
            <StampIconDisplay annotation={ghostAnnotation} />
          </div>
        </div>
      )}
    </>
  )
}

export function StampAnnotationElement({
  annotation,
  page,
  status,
  send
}: {
  annotation: StampAnnotation
  page: PageStructure
  status: ElementStatus
  send: (message: ToolMessage<StampAnnotation, StampToolState>) => void
}) {
  switch (status) {
    case 'selected':
    case 'editing':
      return <StampAnnotationSelected annotation={annotation} send={send} />
    case 'none':
      return (
        <TextStampAnnotationView
          annotation={annotation}
          onSelect={() => send({ type: 'selectAnnotation', annotationId: annotation.id })}
        />
      )
  }
}

function TextStampAnnotationView({
  annotation,
  onSelect
}: {
  annotation: StampAnnotation
  onSelect: () => void
}) {
  // we do not want to allow users to select sign-off stamps and mess with them
  // it will just cause confusion
  const selectable = annotation.stampType != 'sign_off'

  return (
    <ViewElementContainer
      key={annotation.id}
      bounds={annotation.bounds}
      onClick={selectable ? onSelect : undefined}
    >
      <StampAnnotationDisplay annotation={annotation} />
    </ViewElementContainer>
  )
}

function StampAnnotationDisplay({ annotation }: { annotation: StampAnnotation }) {
  return <StampIconDisplay annotation={annotation} />
}

function StampAnnotationSelected({
  annotation,
  send
}: {
  annotation: StampAnnotation
  send: (message: ToolMessage<StampAnnotation, StampToolState>) => void
}) {
  const nav = useDocumentViewerState((s) => selReferenceNavigationState(s, annotation))

  const { onMove } = useUpdateBounds({
    initialBounds: annotation.bounds,
    onUpdate: (event) => {
      send({
        type: 'updateAnnotationDraft',
        annotationDraft: { ...annotation, bounds: event.newBounds }
      })
    },
    onCommit: (event) => {
      send({
        type: 'updateAnnotationDraft',
        annotationDraft: { ...annotation, bounds: event.newBounds }
      })
    }
  })

  return (
    <SelectedElementContainer
      bounds={annotation.bounds}
      onMove={onMove}
      resizable={false}
      onDoubleClick={() => {
        send({ type: 'beginEditingAnnotation', annotationDraft: annotation })
      }}
      toolbar={
        <AnnotationFloatingToolbar>
          {annotation.stampType != 'cross_reference' && (
            <FloatingToolbarButton
              icon={<FontSizeSmallerIcon className="size=6" />}
              onClick={() => {
                const newAnnotation: StampAnnotation = recomputeStampLayoutAndStyle({
                  ...annotation,
                  stampSize: annotation.stampSize - 2
                })

                send({
                  type: 'updateAnnotationDraft',
                  annotationDraft: newAnnotation
                })
                send({ type: 'updateTool', tool: copyToolState(newAnnotation) })
              }}
            />
          )}
          {annotation.stampType != 'cross_reference' && (
            <FloatingToolbarButton
              icon={<FontSizeLargerIcon className="size-6" />}
              onClick={() => {
                const newAnnotation: StampAnnotation = recomputeStampLayoutAndStyle({
                  ...annotation,
                  stampSize: annotation.stampSize + 2
                })

                send({
                  type: 'updateAnnotationDraft',
                  annotationDraft: newAnnotation
                })
                send({ type: 'updateTool', tool: copyToolState(newAnnotation) })
              }}
            />
          )}
          {nav && (
            <FloatingToolbarButton
              disabled={!nav.prev}
              icon={<ArrowLeftIcon className="size-6" />}
              onClick={() => {
                send({ type: 'selectAnnotation', annotationId: nav.prev!.id })
                send({ type: 'scrollToAnnotation', annotationId: nav.prev!.id })
              }}
            />
          )}
          {nav && (
            <FloatingToolbarButton
              disabled={!nav.next}
              icon={<ArrowRightIcon className="size-6" />}
              onClick={() => {
                send({ type: 'selectAnnotation', annotationId: nav.next!.id })
                send({ type: 'scrollToAnnotation', annotationId: nav.next!.id })
              }}
            />
          )}
          <FloatingToolbarButton
            icon={<DeleteIcon className="size-6" />}
            onClick={() => {
              send({ type: 'deleteAnnotation', annotationId: annotation.id })
            }}
          />
        </AnnotationFloatingToolbar>
      }
    >
      <StampAnnotationDisplay annotation={annotation} />
    </SelectedElementContainer>
  )
}

function StampOptionPreview({
  template,
  showDescription = true
}: {
  template: StampTemplate
  showDescription?: boolean
}) {
  return (
    <div className="flex h-full w-full items-center gap-2">
      <div className="flex size-10 items-center justify-center">
        <StampIconDisplay annotation={template.annotation} />
      </div>
      {showDescription && (
        <span className="flex items-center text-base font-normal text-gray-700">
          {template.label}
        </span>
      )}
    </div>
  )
}

function StampIconDisplay({ annotation }: { annotation: StampAnnotation }) {
  return (
    <div
      style={{
        position: 'relative',
        width: annotation.bounds.width,
        height: annotation.bounds.height
      }}
    >
      <Viewport
        Surface={HTMLSurface}
        width={annotation.bounds.width}
        height={annotation.bounds.height}
      >
        <StampView annotation={annotation} />
      </Viewport>
    </div>
  )
}

type ReferenceNavigationState = {
  prev?: StampAnnotation
  next?: StampAnnotation
}
function selReferenceNavigationState(
  state: DocumentViewerState,
  anchorAnnot: StampAnnotation
): ReferenceNavigationState | undefined {
  if (anchorAnnot.stampType != 'cross_reference') {
    return undefined
  }

  const annotations = selAnnotations(state, (el): el is StampAnnotation => {
    const a = el as DocumentAnnotation
    return (
      a.type == 'stamp' && a.stampType == 'cross_reference' && a.stampCode == anchorAnnot.stampCode
    )
  })

  var prev: StampAnnotation | undefined = undefined
  var next: StampAnnotation | undefined = undefined

  for (const cur of Object.values(annotations)) {
    if (cur.id == anchorAnnot.id) {
      continue
    } else if (cur.bounds.y < anchorAnnot.bounds.y) {
      if (verticalDistance(anchorAnnot, cur) < verticalDistance(anchorAnnot, prev)) {
        prev = cur
      }
    } else if (cur.bounds.y > anchorAnnot.bounds.y) {
      if (verticalDistance(anchorAnnot, cur) < verticalDistance(anchorAnnot, next)) {
        next = cur
      }
    }
  }

  return { prev, next }

  function verticalDistance(a: StampAnnotation | undefined, b: StampAnnotation | undefined) {
    if (!a || !b) {
      return Number.MAX_SAFE_INTEGER
    }
    return Math.abs(a.bounds.y - b.bounds.y)
  }
}
